import { ICoordinates } from '../../../interfaces/geo';
import { FishingWater } from '../types/FishingWater';
import { SuggestedWaterFormState } from '../types/SuggestedWaterForm';

/**
 * Retrieves the suggested location if it exists and is different than the current one.
 * @param location - The current coordinates.
 * @param newLocation - The new coordinates.
 * @returns The suggested coordinates or undefined if no suggestion was made.
 */
export const getLocationSuggestion = (
  location: ICoordinates,
  newLocation?: ICoordinates,
): ICoordinates | undefined => {
  if (
    newLocation &&
    (location.latitude !== newLocation.latitude || location.longitude !== newLocation.longitude)
  ) {
    return newLocation;
  }
  return undefined;
};

export const getNewMetadataSuggestion = (
  newMetadata: Record<string, string>,
): string[] | undefined => {
  const metaDataSuggestion = Object.keys(newMetadata)
    .map(group => newMetadata[group])
    .filter(group => !!group);

  if (metaDataSuggestion.length > 0) {
    return metaDataSuggestion;
  }
  return undefined;
};

/**
 * Retrieves the suggested metadata if its different than the current one.
 * This helper will go through all metadata groups and check if there was a change in the groups metada.
 * @param metadata - The current metadata.
 * @param newMetadata - The new metadata.
 * @returns The suggested metadata or undefined if no suggestion was made.
 */
export const getMetadataSuggestion = (
  metadata: {
    groupId: string;
    id: string;
  }[],
  newMetadata: Record<string, string>,
): string[] | undefined => {
  const metaDataSuggestion = Object.keys(newMetadata)
    .filter(
      group =>
        newMetadata[group] &&
        newMetadata[group] !== metadata.find(meta => meta.groupId === group)?.id,
    )
    .map(group => newMetadata[group]);
  if (metaDataSuggestion.length > 0) {
    return metaDataSuggestion;
  }
  return undefined;
};

/**
 * Retrieves the suggested water name if it exists and is different than the current one.
 * @param waterName - The current water name.
 * @param newWaterName - The new water name.
 * @returns The suggested water name or undefined if no suggestion was made.
 */
export const getWaterNameSuggestion = (
  waterName: string,
  newWaterName: string,
): string | undefined => {
  if (newWaterName !== '' && newWaterName !== waterName) {
    return newWaterName;
  }
  return undefined;
};

/**
 * Retrieves the suggested URL if it exists and is different than the current one.
 * @param url - The current URL.
 * @param newUrl - The new URL.
 * @returns The suggested URL or undefined if no suggestion was made.
 */
export const getUrlSuggestion = (
  url: string | undefined,
  newUrl: string | undefined,
): string | undefined => {
  if (!newUrl) {
    return undefined;
  }
  if (newUrl !== '' && newUrl !== url) {
    return newUrl;
  }
  return undefined;
};

/**
 * Validates the suggestions for a suggested water form.
 * @param formData - The form data.
 * @param fishingwater - The fishing water data.
 * @returns True if any suggestion was made, false otherwise.
 */
export const hasSuggestion = ({
  formData,
  fishingwater,
}: {
  formData: SuggestedWaterFormState;
  fishingwater: FishingWater;
}): boolean => {
  const hasAdditionalInfo = formData.additionalInformation && formData.additionalInformation !== '';
  const hasAttachments = formData.attachments && formData.attachments.length > 0;

  return (
    !!getWaterNameSuggestion(fishingwater.name, formData.newWaterName) ||
    !!getUrlSuggestion(fishingwater.url, formData.url) ||
    !!getMetadataSuggestion(fishingwater.metadata, formData.fishingWaterMetadata) ||
    !!getLocationSuggestion(fishingwater.coordinates, formData.position) ||
    hasAdditionalInfo ||
    hasAttachments
  );
};
